import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CampaignCreationWizard from '../components/campaigns/CampaignCreationWizard';
// AuthenticatedLayout is provided by AuthenticatedRoute wrapper
import { CampaignV2 } from '../types';
import { supabase } from '../supabase/client';
import type { Campaign } from '../types/Campaign';
import { useTrialContext } from '../hooks/useTrialContext';

// EPOCH 59: Helper function to get campaign ID from URL using stable browser API
// useSearchParams can return null during auth state changes, so we use window.location as fallback
function getStableCampaignIdFromUrl(): { editId: string | null; viewId: string | null } {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    editId: urlParams.get('edit'),
    viewId: urlParams.get('view')
  };
}

const CreateCampaignWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isTrialExpired, isPaidLapsed } = useTrialContext();
  const [existingCampaign, setExistingCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState<string | null>(null);

  // EPOCH 59: Track loaded campaign to prevent re-initialization on auth state changes
  const loadedCampaignIdRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);

  // EPOCH 59: Use stable URL reading with fallback to window.location
  // This prevents losing campaign context when useSearchParams returns null during re-renders
  const { editCampaignId, viewCampaignId, campaignId, isViewMode, isEditMode } = useMemo(() => {
    // First try React Router's searchParams
    let editId = searchParams.get('edit');
    let viewId = searchParams.get('view');

    // EPOCH 59: If React Router returns null, fallback to window.location (stable browser API)
    if (!editId && !viewId) {
      const stable = getStableCampaignIdFromUrl();
      if (stable.editId || stable.viewId) {
        console.log('📋 EPOCH59: useSearchParams returned null, using window.location fallback');
        editId = stable.editId;
        viewId = stable.viewId;
      }
    }

    return {
      editCampaignId: editId,
      viewCampaignId: viewId,
      campaignId: editId || viewId,
      isViewMode: !!viewId,
      isEditMode: !!editId
    };
  }, [searchParams]);

  const loadExistingCampaign = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .select('*')
        .eq('id', id)
        .single();

      if (campaignError) throw campaignError;

      // Check if campaign status allows editing
      if (isEditMode) {
        const editableStatuses = ['draft', 'submitted', 'preparing'];
        if (!editableStatuses.includes(campaign.status)) {
          throw new Error(`Cannot edit campaign with status '${campaign.status}'. Only campaigns with status 'draft', 'submitted', or 'preparing' can be edited.`);
        }
      }

      setExistingCampaign(campaign);
    } catch (err) {
      console.error('Error loading campaign:', err);
      setError(err instanceof Error ? err.message : 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [isEditMode]);

  useEffect(() => {
    // EPOCH 59: Prevent re-initialization when auth state changes cause re-renders
    // Only load campaign if:
    // 1. We have a campaign ID AND haven't loaded it yet
    // 2. OR campaign ID changed (user navigated to different campaign)
    if (campaignId) {
      if (loadedCampaignIdRef.current !== campaignId) {
        console.log('📋 Loading campaign data for ID:', campaignId);
        loadedCampaignIdRef.current = campaignId;
        loadExistingCampaign(campaignId);
      } else {
        console.log('📋 EPOCH59: Skipping reload - campaign already loaded:', campaignId);
        // Campaign already loaded, just ensure we're not in loading state
        if (loading) {
          setLoading(false);
        }
      }
    } else if (!hasInitializedRef.current) {
      // Only start fresh wizard on first mount when there's truly no campaign ID
      console.log('📋 No campaign ID, starting fresh wizard');
      hasInitializedRef.current = true;
      setLoading(false);
    } else {
      // EPOCH 59: We've already initialized, don't reset to fresh state
      // This handles the case where useSearchParams temporarily returns null
      console.log('📋 EPOCH59: Ignoring null campaignId - already initialized');
    }
  }, [campaignId, loadExistingCampaign, loading]);

  const handleCampaignComplete = (campaign: CampaignV2) => {
    if (isEditMode) {
      // Navigate to campaigns list with update message
      navigate('/campaigns', { 
        state: { 
          message: `Campaign "${campaign.name}" updated successfully!`,
          type: 'success' 
        } 
      });
    } else {
      // Navigate to campaigns list with creation message
      navigate('/campaigns', { 
        state: { 
          message: `Campaign "${campaign.name}" created successfully!`,
          type: 'success' 
        } 
      });
    }
  };

  const handleCancel = () => {
    navigate('/campaigns');
  };

  // Epoch 74: Block expired trial users from creating campaigns
  // Epoch 85: also blocks lapsed offline-paid accounts (same hard block, reused)
  if (isTrialExpired || isPaidLapsed) {
    return (
      <div className="max-w-2xl mx-auto mt-12 text-center p-8 bg-white rounded-lg shadow">
        <h2 className="text-xl font-bold text-gray-900 mb-3">{isPaidLapsed ? 'Subscription Expired' : 'Trial Expired'}</h2>
        <p className="text-gray-600 mb-6">
          {isPaidLapsed
            ? 'Your subscription has lapsed. Renew to create new campaigns.'
            : 'Your free trial has ended. Upgrade to a paid plan to create new campaigns.'}
        </p>
        <button
          onClick={() => navigate('/pricing')}
          className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700"
        >
          View Plans & Upgrade
        </button>
      </div>
    );
  }

  // Show loading state while campaign data is being fetched
  // This prevents the wizard from rendering with incomplete data
  if (loading) {
    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">
              {campaignId ? 'Restoring your campaign draft...' : 'Initializing wizard...'}
            </p>
          </div>
        </div>
    );
  }

  if (error) {
    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-medium text-gray-900 mb-2">Error Loading Campaign</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/campaigns')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Back to Campaigns
            </button>
          </div>
        </div>
    );
  }

  return (
      <CampaignCreationWizard 
        onComplete={handleCampaignComplete}
        onCancel={handleCancel}
        existingCampaign={existingCampaign}
        isViewMode={isViewMode}
        isEditMode={isEditMode}
      />
  );
};

export default CreateCampaignWizard;